/**
 * Sprint V103.ch (Fr 02.10.2026) — Ablefy-Return-Page.
 *
 * Route: /challenge/willkommen (= Ablefy "Danke-Seite" / Produkt-Redirect-URL)
 *
 * Diese Seite ist die Ablefy-Return-URL fuer das Produkt 519121 (Pro Solo Monat).
 * Sie wird von ZWEI Nutzerkohorten angesprungen:
 *
 *   (A) Challenge-Teilnehmer — kam ueber /challenge-join, hat `challenge_joined_at`.
 *       Zeigt: "Dein Platz in der Challenge ist da. Montag 07:00 geht Tag 1 los."
 *
 *   (B) Laufkundschaft — kam direkt ueber /pricing, hat KEIN Challenge-Merkmal.
 *       Zeigt: "Willkommen in Pro Solo — dein Zugang laeuft."
 *
 * Je nach User-Zustand branchen wir die Copy. Negativtest: Laufkunde sieht
 * NIEMALS "Montag geht's los" (das waere das einzige echte Risiko).
 *
 * Fuer Fall (B) ohne aktive Session (nicht eingeloggter Direktkauf) rendern
 * wir eine neutrale Variante mit Login-Hinweis.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Calendar, Sparkles, ArrowRight, LogIn } from "lucide-react";
import { api } from "@/lib/api";

export default function ChallengeWillkommen() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, user: null, pollCount: 0 });

  // Pollt /auth/me max. 10x (= 10 s) bis entweder challenge_joined_at
  // ODER license_tier=trial gesetzt ist (Webhook-Latenz-Fallback).
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const MAX_POLLS = 10;

    const poll = async (count) => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        const joined = Boolean(data?.challenge_joined_at);
        const trial = (data?.license_tier || "").toLowerCase() === "trial";
        if (joined || trial || count >= MAX_POLLS) {
          setState({ loading: false, user: data, pollCount: count });
        } else {
          timer = setTimeout(() => poll(count + 1), 1000);
        }
      } catch (err) {
        // Nicht eingeloggt (401) — Laufkunde ohne vorige Session.
        // Trotzdem freundlich begruessen (neutrale Variante).
        if (!cancelled) setState({ loading: false, user: null, pollCount: count });
      }
    };
    poll(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (state.loading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-emerald-50 via-amber-50 to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center px-4"
        data-testid="challenge-willkommen-page"
        data-view-mode="loading"
      >
        <div className="text-center" data-testid="challenge-willkommen-loading">
          <Loader2 className="w-12 h-12 animate-spin text-[#F3651D] mx-auto mb-4" />
          <p className="text-base text-slate-700 dark:text-slate-300">
            Wir aktivieren deinen Zugang&nbsp;…
          </p>
        </div>
      </div>
    );
  }

  const user = state.user;
  const isLoggedIn = Boolean(user);
  const isChallengeMember = Boolean(user?.challenge_joined_at);

  // Branch-Entscheidung: 3 Varianten
  //   challenge  -> hat `challenge_joined_at`
  //   pro        -> eingeloggt, aber kein Challenge-Merkmal
  //   anonymous  -> nicht eingeloggt (Direkt-Checkout ohne Session)
  const viewMode = isChallengeMember ? "challenge" : (isLoggedIn ? "pro" : "anonymous");

  const trialEndsAt = user?.trial_ends_at;
  const trialEndsAtLabel = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-amber-50 to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"
      data-testid="challenge-willkommen-page"
      data-view-mode={viewMode}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">

        {/* --- Branch 1: Challenge-Teilnehmer -------------------------------- */}
        {viewMode === "challenge" && (
          <>
            <div className="text-center mb-8">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <p
                className="text-xs sm:text-sm font-bold tracking-[0.18em] text-[#F3651D] uppercase mb-3"
                data-testid="challenge-willkommen-kicker"
              >
                5-Tage-Challenge · Mo 05.10. – Fr 09.10.
              </p>
              <h1
                className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white leading-tight"
                data-testid="challenge-willkommen-headline"
              >
                Du bist dabei. Dein Platz ist sicher.
              </h1>
            </div>

            <div
              className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-emerald-200 dark:border-emerald-900 shadow-xl overflow-hidden"
              data-testid="challenge-willkommen-challenge-card"
            >
              <div className="p-6 sm:p-8">
                <div className="mb-6" data-testid="challenge-willkommen-greeting">
                  <p className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {(user?.first_name || "").trim()
                      ? `Hallo ${user.first_name.trim()}!`
                      : "Hallo!"}
                  </p>
                  <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed">
                    Du hast den ersten Schritt gemacht. Ab Montag schließen wir gemeinsam die Lücke
                    zwischen dem, was du sagen willst, und dem, was bei deinem Gegenüber ankommt.
                  </p>
                  <p className="mt-3 text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed">
                    Dein persönlicher PocketCoach steht für dich bereit.
                  </p>
                </div>

                <h2 className="text-xs sm:text-sm font-bold tracking-[0.14em] text-slate-500 dark:text-slate-400 uppercase mb-5 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#F3651D]" />
                  Was jetzt passiert
                </h2>

                <ol className="space-y-5 text-sm sm:text-base text-slate-700 dark:text-slate-200">
                  <li className="flex items-start gap-4" data-testid="challenge-willkommen-step-1">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F3651D]/10 text-[#F3651D] font-extrabold flex items-center justify-center text-sm">
                      1
                    </span>
                    <div>
                      <b className="text-slate-900 dark:text-white block mb-1">
                        Deine Bestätigung ist im Postfach
                      </b>
                      <span className="leading-relaxed">
                        Du erhältst in wenigen Minuten eine E-Mail mit deinen Zugangsdaten für deinen
                        PocketCoach. Wenn du schon ein Konto hast, ist die Challenge dort bereits
                        hinterlegt.
                      </span>
                    </div>
                  </li>

                  <li className="flex items-start gap-4" data-testid="challenge-willkommen-step-2">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F3651D]/10 text-[#F3651D] font-extrabold flex items-center justify-center text-sm">
                      2
                    </span>
                    <div>
                      <b className="text-slate-900 dark:text-white block mb-1">
                        Am Montag um 07:00 Uhr öffnen wir Tag 1
                      </b>
                      <span className="leading-relaxed">
                        Jeden Morgen erwartet dich ein neuer, kurzer Impuls direkt in deinem
                        Mitgliederbereich. Fünf Minuten, die du direkt an deinen echten Alltagssituationen
                        anwendest.
                      </span>
                    </div>
                  </li>

                  <li className="flex items-start gap-4" data-testid="challenge-willkommen-step-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F3651D]/10 text-[#F3651D] font-extrabold flex items-center justify-center text-sm">
                      3
                    </span>
                    <div>
                      <b className="text-slate-900 dark:text-white block mb-1">
                        Was du dir erarbeitest, gehört dir
                      </b>
                      <span className="leading-relaxed">
                        Deine Notizen, deine erkannten Muster und deine Gesprächsvorlagen bleiben
                        in deinem Mitgliederbereich gespeichert &mdash; auch nach den fünf Tagen.
                      </span>
                    </div>
                  </li>
                </ol>

                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  data-testid="challenge-willkommen-cockpit-btn"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white font-extrabold text-base sm:text-lg px-6 py-4 shadow-md transition-all"
                >
                  Jetzt direkt zum PocketCoach <ArrowRight className="w-5 h-5" />
                </button>

                <div
                  className="mt-7 pt-6 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-600 dark:text-slate-300 leading-relaxed"
                  data-testid="challenge-willkommen-footer"
                >
                  <p>
                    Fragen oder etwas hakt? Antworte einfach direkt auf meine E-Mails. Ich lese
                    jede Nachricht persönlich.
                  </p>
                  <p className="mt-3">
                    Bis Montag früh,
                    <br />
                    <b className="text-slate-900 dark:text-white">Andreas</b>
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* --- Branch 2: Laufkundschaft (Pro-Kauf ohne Challenge) ------------ */}
        {viewMode === "pro" && (
          <>
            <div className="text-center mb-8">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h1
                className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white leading-tight"
                data-testid="challenge-willkommen-headline"
              >
                Willkommen in Pro Solo.
              </h1>
              <p className="mt-3 text-base text-slate-700 dark:text-slate-300">
                Dein Zugang läuft &mdash; alles freigeschaltet.
              </p>
            </div>

            <div
              className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-emerald-200 dark:border-emerald-900 shadow-xl overflow-hidden"
              data-testid="challenge-willkommen-pro-card"
            >
              <div className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-5 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F3651D]" />
                  Dein PocketCoach ist bereit
                </h2>

                <ul className="space-y-4 text-sm text-slate-700 dark:text-slate-200">
                  {trialEndsAtLabel && (
                    <li className="flex items-start gap-3" data-testid="challenge-willkommen-step-trial-end">
                      <Calendar className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div>
                        Dein 7-Tage-Test endet am <b className="text-slate-900 dark:text-white">{trialEndsAtLabel}</b>.
                        Danach startet dein reguläres Monatsabo &mdash; jederzeit kündbar.
                      </div>
                    </li>
                  )}
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      Volle Pro-Features sind freigeschaltet &mdash; Fokustyp-Diagnose, Co-Creator, Historie.
                    </div>
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  data-testid="challenge-willkommen-cockpit-btn"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white font-extrabold text-base sm:text-lg px-6 py-4 shadow-md transition-all"
                >
                  Zum PocketCoach <ArrowRight className="w-5 h-5" />
                </button>

                <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 text-center">
                  Du erhältst in Kürze eine Bestätigungs-Mail von Ablefy.
                </p>
              </div>
            </div>
          </>
        )}

        {/* --- Branch 3: Anonym (Direkt-Checkout ohne eingeloggte Session) --- */}
        {viewMode === "anonymous" && (
          <>
            <div className="text-center mb-8">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h1
                className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white leading-tight"
                data-testid="challenge-willkommen-headline"
              >
                Danke &mdash; dein Zugang ist aktiviert.
              </h1>
              <p className="mt-3 text-base text-slate-700 dark:text-slate-300">
                Melde dich bitte mit deiner E-Mail-Adresse an, um loszulegen.
              </p>
            </div>

            <div
              className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden"
              data-testid="challenge-willkommen-anon-card"
            >
              <div className="p-6 sm:p-8">
                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                  Wir schicken dir in Kürze eine Bestätigungs-Mail von Ablefy &mdash;
                  darin findest du auch den Link zu deinem PocketCoach. Alternativ kannst
                  du dich direkt per E-Mail-Login einwählen:
                </p>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  data-testid="challenge-willkommen-login-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white font-extrabold text-base sm:text-lg px-6 py-4 shadow-md transition-all"
                >
                  <LogIn className="w-5 h-5" />
                  Jetzt einloggen <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
